import React, { useMemo, useState } from 'react';
import { Space, Button, Tag, Typography, Modal, Input, message } from 'antd';
import { UploadOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import EnhancedTableWrapper from '../../../components/EnhancedTableWrapper';
import { financeDefaultColumns } from './financeColumns.config';
import {
  FinanceRecordWithDetails,
  ExpenseType,
  PaymentNature,
  EntityType,
  updateFinanceRemark,
} from '../../../services/finance';

const { Text } = Typography;
const { TextArea } = Input;

// 费用类型映射
const EXPENSE_TYPE_MAP: Record<ExpenseType, string> = {
  [ExpenseType.CONSULTATION_FEE]: '咨询费',
  [ExpenseType.LAWYER_FEE]: '律师费',
  [ExpenseType.CONSULTANT_FEE]: '顾问费',
  [ExpenseType.SERVICE_FEE]: '服务费',
  [ExpenseType.TRAVEL_FEE]: '差旅费',
  [ExpenseType.OTHER]: '其他',
};

// 款项类型映射
const PAYMENT_NATURE_MAP: Record<PaymentNature, string> = {
  [PaymentNature.RECEIVED]: '已收款',
  [PaymentNature.RECEIVABLE]: '应收款',
  [PaymentNature.REFUND]: '退款',
};

// 实体类型映射
const ENTITY_TYPE_MAP: Record<EntityType, string> = {
  [EntityType.LEAD]: '线索',
  [EntityType.CASE]: '案件',
};

const getPaymentNatureColor = (nature: PaymentNature) => {
  const colorMap: Record<PaymentNature, string> = {
    [PaymentNature.RECEIVED]: 'success',
    [PaymentNature.RECEIVABLE]: 'warning',
    [PaymentNature.REFUND]: 'error',
  };
  return colorMap[nature] || 'default';
};

interface FinanceTableEnhancedProps {
  records: FinanceRecordWithDetails[];
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number, pageSize: number) => void;
  onUploadInvoice: (record: FinanceRecordWithDetails) => void;
  onViewEntity: (entityType: EntityType, entityId: number) => void;
  renderAttachments: (attachments: any[]) => React.ReactNode;
  currentUserRole?: string;
  onRemarkUpdated?: () => void;
}

const FinanceTableEnhanced: React.FC<FinanceTableEnhancedProps> = ({
  records,
  loading,
  page,
  pageSize,
  total,
  onPageChange,
  onUploadInvoice,
  onViewEntity,
  renderAttachments,
  currentUserRole,
  onRemarkUpdated,
}) => {
  // 备注编辑状态
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<FinanceRecordWithDetails | null>(null);
  const [editingRemarkValue, setEditingRemarkValue] = useState<string>('');
  const [savingRemark, setSavingRemark] = useState(false);

  // 备注编辑处理函数
  const handleEditRemark = (record: FinanceRecordWithDetails) => {
    setEditingRecord(record);
    setEditingRemarkValue(record.remark || '');
    setEditModalVisible(true);
  };

  const handleSaveRemark = async () => {
    if (!editingRecord) return;

    setSavingRemark(true);
    try {
      await updateFinanceRemark(editingRecord.id, editingRemarkValue);
      message.success('备注更新成功');
      setEditModalVisible(false);
      setEditingRecord(null);
      setEditingRemarkValue('');
      onRemarkUpdated?.();
    } catch (error: any) {
      message.error(error.message || '更新备注失败');
    } finally {
      setSavingRemark(false);
    }
  };

  const handleCancelEditRemark = () => {
    setEditModalVisible(false);
    setEditingRecord(null);
    setEditingRemarkValue('');
  };

  const columnRenderMap: Record<string, any> = useMemo(() => ({
    index: {
      render: (_: any, __: any, index: number) => (page - 1) * pageSize + index + 1,
    },
    contact_name: {
      render: (name: string) => name || '-',
    },
    case_name: {
      ellipsis: { showTitle: false },
      render: (name: string, record: FinanceRecordWithDetails) => (
        <Space direction="vertical" size={0} style={{ width: '100%' }}>
          <Text ellipsis={{ tooltip: name }} style={{ maxWidth: '100%' }}>
            {name || '-'}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {ENTITY_TYPE_MAP[record.entity_type]}
          </Text>
        </Space>
      ),
    },
    expense_type: {
      render: (type: ExpenseType) => EXPENSE_TYPE_MAP[type] || type,
    },
    amount: {
      render: (amount: string | number, record: FinanceRecordWithDetails) => {
        const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        const color = record.payment_nature === PaymentNature.REFUND ? '#ff4d4f' : '#52c41a';
        const prefix = record.payment_nature === PaymentNature.REFUND ? '-' : '+';
        return (
          <Text strong style={{ color }}>
            {prefix}￥{numAmount.toLocaleString()}
          </Text>
        );
      },
      sorter: (a: FinanceRecordWithDetails, b: FinanceRecordWithDetails) => {
        const amountA = typeof a.amount === 'string' ? parseFloat(a.amount) : a.amount;
        const amountB = typeof b.amount === 'string' ? parseFloat(b.amount) : b.amount;
        return amountA - amountB;
      },
    },
    payment_time: {
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-',
      sorter: (a: FinanceRecordWithDetails, b: FinanceRecordWithDetails) => {
        if (!a.payment_time) return 1;
        if (!b.payment_time) return -1;
        return dayjs(a.payment_time).unix() - dayjs(b.payment_time).unix();
      },
    },
    payment_nature: {
      render: (nature: PaymentNature) => (
        <Tag color={getPaymentNatureColor(nature)}>
          {PAYMENT_NATURE_MAP[nature] || nature}
        </Tag>
      ),
    },
    invoices: {
      align: 'center' as const,
      render: (invoices: any[]) => renderAttachments(invoices),
    },
    attachments: {
      align: 'center' as const,
      render: (attachments: any[]) => renderAttachments(attachments),
    },
    responsible_person_name: {
      render: (name: string) => name || '-',
    },
    remark: {
      ellipsis: true,
      render: (remark: string, record: FinanceRecordWithDetails) => {
        // 检查是否有编辑权限（财务和管理员）
        const canEdit = currentUserRole === 'finance' || currentUserRole === 'admin';

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text ellipsis={{ tooltip: remark }} style={{ flex: 1 }}>
              {remark || '-'}
            </Text>
            {canEdit && (
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditRemark(record);
                }}
                title="编辑备注"
              />
            )}
          </div>
        );
      },
    },
    created_at: {
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-',
      sorter: (a: FinanceRecordWithDetails, b: FinanceRecordWithDetails) => {
        if (!a.created_at) return 1;
        if (!b.created_at) return -1;
        return dayjs(a.created_at).unix() - dayjs(b.created_at).unix();
      },
      defaultSortOrder: 'descend' as const,
    },
    action: {
      render: (_: any, record: FinanceRecordWithDetails) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<UploadOutlined />}
            onClick={() => onUploadInvoice(record)}
          >
            上传发票
          </Button>
          {currentUserRole === 'admin' && record.entity_id && (
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => onViewEntity(record.entity_type, record.entity_id)}
            >
              查看详情
            </Button>
          )}
        </Space>
      ),
    },
  }), [page, pageSize, onUploadInvoice, onViewEntity, renderAttachments, currentUserRole, onRemarkUpdated]);

  return (
    <>
      <EnhancedTableWrapper
        viewType="finance-management"
        defaultColumns={financeDefaultColumns}
        columnRenderMap={columnRenderMap}
        dataSource={records}
        loading={loading}
        rowKey="id"
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: onPageChange,
        }}
        scroll={{ x: 1800, y: 600 }}
      />

      {/* 备注编辑弹窗 */}
      <Modal
        title="编辑备注"
        open={editModalVisible}
        onOk={handleSaveRemark}
        onCancel={handleCancelEditRemark}
        confirmLoading={savingRemark}
        okText="保存"
        cancelText="取消"
        width={500}
        bodyStyle={{ paddingBottom: 24 }}
      >
        {editingRecord && (
          <div>
            <div style={{ marginBottom: 16, color: 'rgba(0, 0, 0, 0.45)', fontSize: 13 }}>
              <Text type="secondary">
                联系人：{editingRecord.contact_name || '-'} |
                案件名称：{editingRecord.case_name || '-'} |
                金额：￥{parseFloat(String(editingRecord.amount)).toLocaleString()}
              </Text>
            </div>
            <TextArea
              value={editingRemarkValue}
              onChange={(e) => setEditingRemarkValue(e.target.value)}
              placeholder="请输入备注内容"
              rows={4}
              maxLength={500}
              showCount
              style={{ marginBottom: 8 }}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default FinanceTableEnhanced;
